import React from 'react';
import {Typography} from '@material-ui/core';
import millify from 'millify';
import {getPercents} from '../helpers/dataHelpers';
import {StyledTableCell} from './StyledTableCell';
import Trend from '../../Trend';
import {Skeleton} from '@material-ui/lab';

const formatStackedValue = value => {
  if (typeof value !== 'number' || Number.isNaN(value)) return value;
  return value.toLocaleString('en-US', {maximumFractionDigits: 2});
};

const TableTotals = React.memo(
  ({
    metricsConfig,
    demandData,
    comparisonDemandData,
    comparisonEnabled,
    comparisonLoading,
    enabledMetric,
    toggleMetric,
    theme,
    totalsSuffix = null,
    disableMillify,
    visibleMetrics,
    isMetricVisible,
  }) => {
    const metricsToRender = visibleMetrics || Object.keys(metricsConfig.perSource);
    return metricsToRender
      .filter(metric => !isMetricVisible || isMetricVisible(metric))
      .map(metric => {
        const config = metricsConfig.perSource[metric];
        let totalsKey = config.dataField;

        if (totalsSuffix) {
          totalsKey = `${totalsKey}${totalsSuffix}`;
        }

        // Totals key for percents calculation
        // suffix - totals of enabled sources
        // no suffix - totals of all sources
        const totalsKeyForPercents = totalsSuffix ? 'totalsOfEnabledSources' : 'totals';

        let totalBySourceAndMetric = demandData.totalsOfEnabledSources?.[totalsKey] ?? 0;
        const totalsBySource = demandData[totalsKeyForPercents]?.[config.dataField] ?? 0;

        const previousRaw =
          comparisonDemandData?.totalsOfEnabledSources?.[totalsKey] ??
          comparisonDemandData?.totals?.[totalsKey];
        const previousTotal =
          previousRaw !== undefined && previousRaw !== null ? Number(previousRaw) : null;

        const isMetricActive = enabledMetric.includes(config.dataField);

        if (
          isNaN(totalBySourceAndMetric) ||
          totalBySourceAndMetric === null ||
          totalBySourceAndMetric === undefined
        ) {
          totalBySourceAndMetric = 0;
        }

        return (
          <StyledTableCell
            key={metric}
            className="metricCell"
            style={{
              background: isMetricActive
                ? theme.palette.graphColors.themeDarkGray
                : theme.palette.background.paper,
            }}
            align="left"
            onClick={() => toggleMetric(config.dataField)}
          >
            {!config.disableTotals && (
              <Typography variant={totalsSuffix ? 'body2' : 'h6'} style={{fontWeight: 'bold'}}>
                {formatStackedValue(
                  disableMillify || config.disableMillify
                    ? parseFloat(Number(totalBySourceAndMetric).toFixed(2))
                    : millify(totalBySourceAndMetric)
                )}
                {config.symbol}{' '}
                {comparisonEnabled &&
                  !comparisonLoading &&
                  previousTotal !== null &&
                  !isNaN(previousTotal) &&
                  previousTotal !== 0 && (
                    <>
                      {' '}
                      <Trend
                        invertTrend={config.invertTrend}
                        inDropdown
                        current={Number(totalBySourceAndMetric)}
                        previous={previousTotal}
                      />
                    </>
                  )}
                {comparisonEnabled && comparisonLoading && (
                  <span style={{display: 'inline-block', marginLeft: 4, width: 32}}>
                    <Skeleton variant="rect" height={10} style={{borderRadius: 4}} />
                  </span>
                )}
              </Typography>
            )}

            {config.disableTotals && (
              <Typography variant={totalsSuffix ? 'body2' : 'h6'} style={{fontWeight: 'bold'}}>
                -
              </Typography>
            )}

            {config.cumulative && !config.disableTotals && !config.dayLevelOnly && (
              <Typography variant={'caption'} display="inline" color={'textSecondary'}>
                {getPercents(totalBySourceAndMetric, totalsBySource)}% of Total
              </Typography>
            )}
          </StyledTableCell>
        );
      });
  }
);

export default TableTotals;
